"use client";

import React from "react";
import { motion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, ArrowRight, ShieldCheck, PhoneCall } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const faqs = [{
  id: "item-1",
  question: "Do BMF Scaffolding cover Bracknell?",
  answer: "Yes. We regularly supply and erect scaffolding across Bracknell and surrounding areas throughout Berkshire, Surrey, and the wider Home Counties for residential, commercial, and industrial sites."
}, {
  id: "item-2",
  question: "What types of scaffolding can you provide in Bracknell?",
  answer: "We provide independent access scaffolding, temporary roofs, edge protection, chimney stacks, birdcages, cantilever structures, solar panel access, and bespoke engineered designs for complex builds."
}, {
  id: "item-3",
  question: "Can you provide scaffolding for roof work?",
  answer: "Yes. We construct secure access towers, perimeter handrails, edge protection, and full temporary weather-protection roofs for roofing contractors, solar installers, and private properties."
}, {
  id: "item-4",
  question: "Do you work with contractors and developers?",
  answer: "Yes. We regularly partner with main contractors, developers, local authorities, facilities managers, and specialist trades, operating under full NASC and CHAS compliance standards."
}, {
  id: "item-5",
  question: "Can scaffolding be installed where access is restricted?",
  answer: "Yes. Our team assesses tight alleyways, narrow driveways, delicate gardens, and public walkways during the initial site survey to design safe, compliant access solutions tailored to site constraints."
}, {
  id: "item-6",
  question: "How do I request a quote in Bracknell?",
  answer: "You can submit your project drawings or schedule a site survey through our online quote request form or by contacting our operations team directly with your project specifications."
}];
export default function Faq() {
  return <section id="faq" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-accent/50 text-foreground border-border">
            <HelpCircle className="size-3.5 text-primary mr-1" />
            Common Questions
          </Badge>

          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default mb-4">
            Frequently asked questions
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground font-default">
            Clear facts about our scaffolding coverage, site access, and services across Bracknell.
          </p>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="bg-card text-card-foreground border border-border rounded-xl shadow-xs p-6 sm:p-8 md:p-10 mb-12">
          <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
            {faqs.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="border-b border-border last:border-b-0 py-1">
                <AccordionTrigger className="text-base sm:text-lg font-semibold text-foreground hover:text-primary transition-colors py-4 text-left">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed font-default pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-accent/30 border border-border rounded-xl p-6 sm:p-8 items-center">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <ShieldCheck className="size-5 text-primary" />
              <h3 className="text-lg font-bold text-foreground">
                Need a specific site inspection or survey?
              </h3>
            </div>
            <p className="text-sm text-muted-foreground">
              Speak with Pete Wood and our operations team to discuss site plans, safety requirements, and scheduling.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center md:justify-end gap-3">
            <Button asChild variant="default" size="lg" className="w-full sm:w-auto">
              <Link to="/ContactRequestQuotePage">
                Request a quote
                <ArrowRight className="size-4 ml-1.5" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/AreasWeCoverPage">
                <PhoneCall className="size-4 mr-1.5" />
                Areas we cover
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}